/**
 * Firebase configuration and initialization
 * Centralizes all Firebase service setup for trAInedup.ai
 */

import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { initializeFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// Validate that all required environment variables are present
const requiredEnvVars = [
  'REACT_APP_FIREBASE_API_KEY',
  'REACT_APP_FIREBASE_AUTH_DOMAIN',
  'REACT_APP_FIREBASE_PROJECT_ID',
  'REACT_APP_FIREBASE_STORAGE_BUCKET',
  'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
  'REACT_APP_FIREBASE_APP_ID',
];

const missingEnvVars = requiredEnvVars.filter(
  (envVar) => !process.env[envVar]
);

if (missingEnvVars.length > 0) {
  console.error(
    '🔥 Missing required Firebase environment variables:',
    missingEnvVars
  );
  console.error(
    '📝 Please check your .env file and ensure all Firebase configuration variables are set.'
  );
  console.error(
    '🔧 To fix this:'
  );
  console.error(
    '1. Go to Firebase Console: https://console.firebase.google.com/'
  );
  console.error(
    `2. Select your project: ${firebaseConfig.projectId || 'your-project-id'}`
  );
  console.error(
    '3. Go to Project Settings > General > Your apps'
  );
  console.error(
    '4. Copy the config values to your .env file'
  );
}

// Check for mock values that need to be replaced
const mockValues = [
  'mock_api_key',
  'mock-project',
  'AIzaSyDXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX',
];

const hasMockValues = mockValues.some(mockValue => 
  Object.values(firebaseConfig).some(value => 
    typeof value === 'string' && value.includes(mockValue)
  )
);

if (hasMockValues) {
  console.warn(
    '⚠️  Firebase configuration contains mock values!'
  );
  console.warn(
    '🔧 Please update your .env file with real Firebase credentials.'
  );
  console.warn(
    '📍 Current project ID should be: trainedup-a4915'
  );
}

// Initialize Firebase app
export const app = initializeApp(firebaseConfig);

// Initialize App Check with reCAPTCHA v3 (enabled when a site key is provided)
const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

// Skip App Check during the puppeteer prerender (scripts/prerender.js) — otherwise
// reCAPTCHA Enterprise injects its loader script and badge into the DOM, which gets
// serialized into the deployed static HTML and collides with runtime init in real
// browsers ("reCAPTCHA placeholder element must be empty" + Timeout).
const isPrerender =
  typeof navigator !== 'undefined' &&
  (/HeadlessChrome/i.test(navigator.userAgent) || (navigator as any).webdriver === true);

if (typeof window !== 'undefined' && recaptchaSiteKey && !isPrerender) {
  try {
    // Enable debug token in development or localhost to avoid 400 errors
    // Note: Debug provider is only available in development builds of the Firebase SDK
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    if (process.env.NODE_ENV === 'development' || isLocalhost) {
      // Use the environment variable if set, otherwise default to true (which logs a new token to console)
      // @ts-ignore
      (window as any).FIREBASE_APPCHECK_DEBUG_TOKEN = process.env.REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN || true;
      // eslint-disable-next-line no-console
      console.log('🛡️ App Check: Debug token enabled for ' + (process.env.NODE_ENV === 'development' ? 'development' : 'localhost'));
    }

    if (process.env.NODE_ENV === 'production' && isLocalhost) {
      console.warn('⚠️ Running production build on localhost. App Check may fail unless localhost is whitelisted in reCAPTCHA console.');
    }

    const appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(recaptchaSiteKey),
      isTokenAutoRefreshEnabled: true,
    });

    if (process.env.NODE_ENV !== 'production') {
      (window as any).appCheckInstance = appCheck;
    }
  } catch (error) {
    console.error('⚠️  Failed to initialize Firebase App Check:', error);
  }
} else if (process.env.NODE_ENV === 'production') {
  console.warn('⚠️  Firebase App Check is not initialized. Set REACT_APP_RECAPTCHA_SITE_KEY to enable it.');
}

// Initialize Firebase services
export const auth = getAuth(app);
export const db = initializeFirestore(app, {
  experimentalForceLongPolling: true,
});

// Don't use Firebase Analytics - we're using gtag.js directly from index.html
// This prevents duplicate analytics initialization
// export const analytics = getAnalytics(app);
export const analytics = null as any; // Placeholder to prevent import errors

export const storage = getStorage(app);
export const functions = getFunctions(app, 'us-central1');

// Connect to Firebase emulators in development
if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_FIREBASE_EMULATORS === 'true') {
  try {
    // Connect to Auth emulator
    connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
    
    // Connect to Firestore emulator
    connectFirestoreEmulator(db, 'localhost', 8080);
    
    // Connect to Storage emulator
    connectStorageEmulator(storage, 'localhost', 9199);
    
    // Connect to Functions emulator
    connectFunctionsEmulator(functions, 'localhost', 5001);
    
  } catch (error) {
    console.warn('⚠️  Could not connect to Firebase emulators:', error);
    console.warn('💡 Make sure to run "firebase emulators:start" if you want to use emulators');
  }
} else {
  // Using production Firebase services
}

// Export Firebase app instance for use in other parts of the application
export default app;

